import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Select,
  SelectItem,
} from '@heroui/react';
import {
  Typography,
  CustomHeroButton,
  CustomCheckbox,
  ResponsiveModal,
} from '../../../../components';
import { useTranslations } from '../../../../contexts/translation.context';
import CustomHeroInput from '../../../../components/CustomHeroInput';
import OfferSelector from './OfferSelector';
import { PublicLink } from '../../../../api/partner/partnerOfferService';

interface AddPublicLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: PublicLinkFormData, linkId?: string) => void;
  editLink?: PublicLink | null;
  onDelete?: (linkId: string) => Promise<void> | void;
}

export interface PublicLinkFormData {
  name: string;
  orderIds: string[];
  showRevenue: boolean;
  showIncome: boolean;
  showCosts: boolean;
  active: boolean;
}

const AddPublicLinkModal: React.FC<AddPublicLinkModalProps> = ({
  isOpen,
  onClose,
  onSave,
  editLink,
  onDelete,
}) => {
  const { t } = useTranslations();
  const [formData, setFormData] = useState<PublicLinkFormData>({
    name: '',
    orderIds: [],
    showRevenue: false,
    showIncome: false,
    showCosts: false,
    active: true,
  });
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (editLink && isOpen) {
      setFormData({
        name: editLink.name || '',
        orderIds: editLink.campaigns.map((id) => String(id)),
        showRevenue: editLink.revenue,
        showIncome: editLink.income,
        showCosts: editLink.costs,
        active: editLink.active,
      });
    }
  }, [editLink, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setIsDeleteConfirmOpen(false);
      setIsDeleting(false);
    }
  }, [isOpen]);

  const handleSave = () => {
    onSave(formData, editLink?.id);
    handleClose();
  };

  const handleClose = () => {
    setFormData({
      name: '',
      orderIds: [],
      showRevenue: false,
      showIncome: false,
      showCosts: false,
      active: true,
    });
    onClose();
  };

  const handleConfirmDelete = async () => {
    if (!editLink?.id || !onDelete) {
      return;
    }

    try {
      setIsDeleting(true);
      await onDelete(editLink.id);
      setIsDeleteConfirmOpen(false);
      handleClose();
    } catch (error) {
      console.error('Error deleting public link:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const openDeleteConfirmation = () => {
    if (!editLink?.id || !onDelete) {
      return;
    }
    setIsDeleteConfirmOpen(true);
  };
  const statusOptions = [
    { key: 'true', label: t('partner.orders.addPublicLinkModal.active') },
    { key: 'false', label: t('partner.orders.addPublicLinkModal.inactive') },
  ];

  return (
    <>
      <ResponsiveModal
        isOpen={isOpen}
        onClose={handleClose}
        size="lg"
        classNames={{
          base: 'md:max-w-[37.5rem]',
          body: 'p-0',
        }}
      >
        <ModalContent>
          <ModalHeader className="flex flex-col gap-3 px-4 md:px-6 pt-6 pb-4">
            <Typography variant="h6" className="text-auth-ink text-xl font-medium">
              {editLink
                ? t('partner.orders.editPublicLinkModal.title')
                : t('partner.orders.addPublicLinkModal.title')}
            </Typography>
          </ModalHeader>

          <ModalBody className="px-4 md:px-6 py-3">
            <div className="flex flex-col gap-4">
              {/* Название линки */}
              <CustomHeroInput
                label={t('partner.orders.addPublicLinkModal.linkName')}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('partner.orders.addPublicLinkModal.linkNamePlaceholder')}
              />

              {/* Заказы */}
              <OfferSelector
                selectedOrderIds={formData.orderIds}
                onSelectionChange={(orderIds) => setFormData({ ...formData, orderIds })}
              />

              {/* Публичный доступ */}
              <div className="flex flex-col gap-2">
                <Typography variant="body2" className="text-auth-ink">
                  {t('partner.orders.addPublicLinkModal.publicAccess')}
                </Typography>
                <div className="flex flex-wrap gap-4 gap-y-2">
                  <CustomCheckbox
                    isSelected={formData.showRevenue}
                    onValueChange={(checked) => setFormData({ ...formData, showRevenue: checked })}
                    size="md"
                    className="h-full"
                  >
                    {t('partner.orders.addPublicLinkModal.showRevenue')}
                  </CustomCheckbox>
                  <CustomCheckbox
                    isSelected={formData.showIncome}
                    onValueChange={(checked) => setFormData({ ...formData, showIncome: checked })}
                    size="md"
                    className="h-full"
                  >
                    {t('partner.orders.addPublicLinkModal.showIncome')}
                  </CustomCheckbox>
                  <CustomCheckbox
                    isSelected={formData.showCosts}
                    onValueChange={(checked) => setFormData({ ...formData, showCosts: checked })}
                    size="md"
                    className="h-full"
                  >
                    {t('partner.orders.addPublicLinkModal.showCosts')}
                  </CustomCheckbox>
                </div>
              </div>

              {/* Статус */}
              <div className="flex flex-col gap-2">
                <Typography variant="body2" className="text-auth-ink">
                  {t('partner.orders.addPublicLinkModal.status')}
                </Typography>
                <Select
                  selectedKeys={[formData.active.toString()]}
                  onSelectionChange={(keys) => {
                    const value = Array.from(keys)[0] === 'true';
                    setFormData({ ...formData, active: value });
                  }}
                  placeholder={t('partner.orders.addPublicLinkModal.selectStatus')}
                  classNames={{
                    trigger: 'h-[2.375rem] bg-white border-2 border-zinc-300 rounded-lg',
                    value: 'text-sm',
                  }}
                >
                  {statusOptions.map((option) => (
                    <SelectItem key={option.key}>{option.label}</SelectItem>
                  ))}
                </Select>
              </div>
            </div>
          </ModalBody>

          <ModalFooter className="px-4 md:px-6 py-4 gap-2">
            {editLink ? (
              <CustomHeroButton
                className="!bg-auth-strength-weak/20 !text-auth-strength-weak h-10 px-4 rounded-3"
                onPress={openDeleteConfirmation}
                title={t('partner.orders.editPublicLinkModal.delete')}
                disabled={!onDelete}
              />
            ) : (
              <CustomHeroButton
                className="!bg-auth-strength-weak/20 !text-auth-strength-weak h-10 px-4 rounded-3"
                onPress={handleClose}
                title={t('partner.orders.addPublicLinkModal.back')}
              />
            )}
            <CustomHeroButton
              className="bg-partner text-white h-10 px-3.5 rounded-3"
              onPress={handleSave}
              title={t('partner.orders.addPublicLinkModal.save')}
            />
          </ModalFooter>
        </ModalContent>
      </ResponsiveModal>

      {editLink && (
        <ResponsiveModal
          isOpen={isDeleteConfirmOpen}
          onClose={() => setIsDeleteConfirmOpen(false)}
          size="sm"
          classNames={{
            base: 'md:max-w-[25rem]',
          }}
        >
          <ModalContent>
            <ModalHeader className="flex flex-col gap-2 px-4 md:px-6 pt-6 pb-4">
              <Typography variant="h6" className="text-auth-ink text-xl font-medium">
                {t('partner.orders.editPublicLinkModal.deleteConfirmTitle')}
              </Typography>
            </ModalHeader>
            <ModalBody className="px-4 md:px-6">
              <Typography variant="body2" className="text-auth-ink">
                {t('partner.orders.editPublicLinkModal.deleteConfirmDescription', {
                  name: editLink.name || editLink.id,
                })}
              </Typography>
            </ModalBody>
            <ModalFooter className="px-6 pb-6 pt-4 gap-2">
              <CustomHeroButton
                className="!bg-auth-strength-weak/20 !text-auth-strength-weak h-10 px-4 rounded-3"
                onPress={() => setIsDeleteConfirmOpen(false)}
                title={t('partner.orders.editPublicLinkModal.cancel')}
                disabled={isDeleting}
              />
              <CustomHeroButton
                className="bg-partner text-white h-10 px-3.5 rounded-3"
                onPress={() => handleConfirmDelete()}
                title={t('partner.orders.editPublicLinkModal.deleteConfirmAction')}
                isLoading={isDeleting}
                disabled={isDeleting}
              />
            </ModalFooter>
          </ModalContent>
        </ResponsiveModal>
      )}
    </>
  );
};

export default AddPublicLinkModal;
